'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from '@material-bank/mb3-components';
import { AnimatedStep, DrawerDragHandle, useOverlayThemeColor } from '@material-bank/mtag-ui-components';
import { useAuthenticationDialog } from '../../providers/AuthenticationDialogProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import { BenefitsStep } from './steps/BenefitsStep';
import { LoginStep as LoginStepComponent } from './steps/LoginStep';
import { OptionsStep } from './steps/OptionsStep';
import { SuccessStep } from './steps/SuccessStep';
import type { LoginStep } from '../PasswordlessLogin/PasswordlessLogin';
import { clearPendingAuthAction } from '../../lib/pendingAuthAction';
type DialogStep = 'options' | 'login' | 'success';

// Long enough to read "Welcome back" once the check has drawn.
const SUCCESS_HOLD_MS = 1200;
export function AuthenticationDialog() {
  const $ = _c(52);
  const {
    isOpen,
    close,
    dialogOptions
  } = useAuthenticationDialog();
  const {
    resetLoginFlow
  } = useAuthProvider();
  useOverlayThemeColor(isOpen);
  const [dialogStep, setDialogStep] = useState("options");
  const [loginSubStep, setLoginSubStep] = useState("email");
  const successTimerRef = useRef(null);
  const shownStep = dialogStep === "options" && dialogOptions.startWithEmail ? "login" : dialogStep;
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => () => {
      if (successTimerRef.current !== null) {
        window.clearTimeout(successTimerRef.current);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== close || $[3] !== resetLoginFlow) {
    t2 = function closeHandler() {
      clearPendingAuthAction();
      setDialogStep("options");
      setLoginSubStep("email");
      resetLoginFlow();
      close();
    };
    $[2] = close;
    $[3] = resetLoginFlow;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const closeHandler = t2;
  let t3;
  if ($[5] !== close || $[6] !== closeHandler || $[7] !== dialogStep) {
    t3 = function openChangeHandler(open) {
      if (open) {
        return;
      }
      if (dialogStep === "success") {
        close();
        return;
      }
      closeHandler();
    };
    $[5] = close;
    $[6] = closeHandler;
    $[7] = dialogStep;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const openChangeHandler = t3;
  let t4;
  if ($[9] !== loginSubStep) {
    t4 = function backHandler() {
      if (loginSubStep === "otp") {
        setLoginSubStep("email");
        return;
      }
      setDialogStep("options");
    };
    $[9] = loginSubStep;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const backHandler = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function signInHandler() {
      setDialogStep("login");
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const signInHandler = t5;
  let t6;
  if ($[12] !== close) {
    t6 = function successHandler() {
      setDialogStep("success");
      setLoginSubStep("email");
      successTimerRef.current = window.setTimeout(close, SUCCESS_HOLD_MS);
    };
    $[12] = close;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const successHandler = t6;
  let t7;
  if ($[14] !== dialogStep) {
    t7 = function animationEndHandler(open_0) {
      if (!open_0 && dialogStep === "success") {
        setDialogStep("options");
      }
    };
    $[14] = dialogStep;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const animationEndHandler = t7;
  let t8;
  if ($[16] !== closeHandler || $[17] !== dialogOptions.benefits || $[18] !== dialogOptions.description || $[19] !== dialogOptions.icon || $[20] !== dialogOptions.product || $[21] !== dialogOptions.title) {
    t8 = dialogOptions.benefits ? <BenefitsStep title={dialogOptions.title} description={dialogOptions.description} benefits={dialogOptions.benefits} onSignIn={signInHandler} onDismiss={closeHandler} /> : <OptionsStep title={dialogOptions.title} icon={dialogOptions.icon} product={dialogOptions.product} description={dialogOptions.description} onSignIn={signInHandler} />;
    $[16] = closeHandler;
    $[17] = dialogOptions.benefits;
    $[18] = dialogOptions.description;
    $[19] = dialogOptions.icon;
    $[20] = dialogOptions.product;
    $[21] = dialogOptions.title;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const optionsStep = t8;
  let t9;
  if ($[23] !== dialogOptions.title) {
    t9 = <DrawerTitle className="sr-only">{dialogOptions.title}</DrawerTitle>;
    $[23] = dialogOptions.title;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = dialogOptions.description ?? dialogOptions.title;
  let t11;
  if ($[25] !== t10) {
    t11 = <DrawerDescription className="sr-only">{t10}</DrawerDescription>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DrawerDragHandle />;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = shownStep === "options" ? -1 : 1;
  const t14 = shownStep === "options" ? optionsStep : null;
  let t15;
  if ($[28] !== backHandler || $[29] !== dialogOptions.description || $[30] !== dialogOptions.icon || $[31] !== dialogOptions.startWithEmail || $[32] !== dialogOptions.title || $[33] !== loginSubStep || $[34] !== shownStep || $[35] !== successHandler) {
    t15 = shownStep === "login" ? <div className="flex flex-col px-6 pb-6 w-full"><LoginStepComponent step={loginSubStep} intro={dialogOptions.startWithEmail ? {
        title: dialogOptions.title,
        icon: dialogOptions.icon,
        description: dialogOptions.description
      } : undefined} onBack={backHandler} onSuccess={successHandler} onStepChange={setLoginSubStep} /></div> : null;
    $[28] = backHandler;
    $[29] = dialogOptions.description;
    $[30] = dialogOptions.icon;
    $[31] = dialogOptions.startWithEmail;
    $[32] = dialogOptions.title;
    $[33] = loginSubStep;
    $[34] = shownStep;
    $[35] = successHandler;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== shownStep) {
    t16 = shownStep === "success" ? <SuccessStep /> : null;
    $[37] = shownStep;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== shownStep || $[40] !== t13 || $[41] !== t14 || $[42] !== t15 || $[43] !== t16) {
    t17 = <DrawerContent className="rounded-t-[20px] flex flex-col outline-none [&>div:first-child]:hidden">{t12}<div className="min-h-0 overflow-y-auto"><AnimatedStep stepKey={shownStep} direction={t13}>{t14}{t15}{t16}</AnimatedStep></div></DrawerContent>;
    $[39] = shownStep;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== animationEndHandler || $[46] !== isOpen || $[47] !== openChangeHandler || $[48] !== t11 || $[49] !== t17 || $[50] !== t9) {
    t18 = <Drawer open={isOpen} onOpenChange={openChangeHandler} onAnimationEnd={animationEndHandler} repositionInputs={false}>{t9}{t11}{t17}</Drawer>;
    $[45] = animationEndHandler;
    $[46] = isOpen;
    $[47] = openChangeHandler;
    $[48] = t11;
    $[49] = t17;
    $[50] = t9;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
}